import React, { useEffect, useState } from "react";
import BackButton from "../components/BackButton";
import AuthentifiedPage from "./AuthentifiedPage";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import "../styles/CollectDataPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import CollectDataTable from "../components/CollectDataTable";
import { redirect, useLocation, useNavigate } from "react-router-dom";
import DynamicForm from "../components/DynamicForm";
import LocalitySelector from "../components/LocalitySelector";
import { Formulaire } from "../types/Formulaire";
import { useAuth } from "../context/AuthContext";
import { FormulaireService } from "../services/FormulaireService";
import { GroupeImportService } from "../services/GroupeImportService";


const CollectDataPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, domain, sousDomain, sousSousDomain } = location.state || {};

  const {api}=useAuth();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submissions, setSubmissions] = useState<Map<string,any>[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Collecte validée");
  const [errorMessage, setErrorMessage] = useState("Une erreur est survenue");
  const [formulaire, setFormulaire] = useState<Formulaire>({titre:"",fields:[]});
  const [idFenetre, setIdFenetre] = useState(0);
  const [idSousFenetre, setIdSousFenetre] = useState(0);
  const [selectedLocality, setSelectedLocality] = useState<{ idIle?: number; idRegion?: number; idCommune?: number; idLocalite?: number } | null>(null);
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({});
  const [validating, setValidating] = useState(false);
  const [geoPosition, setGeoPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    const loadFormulaire = async () => {
      let f = 0;
      let sf = 0;

      try {
        if (sousSousDomain) {
          sf = sousSousDomain.id;
        } else if (sousDomain) {
          f = sousDomain.id;
        } else {
          redirect("/agent");
          return;
        }

        setIdFenetre(f);
        setIdSousFenetre(sf);

        const data = await FormulaireService.getFormulaire(api, f, sf);

        setFormulaire(data);

        const initialVisibility: Record<string, boolean> = {};

        data.fields.forEach((field) => {
          initialVisibility[field.id] = field.conditionalLogic ? false : true;
        });

        setVisibleFields(initialVisibility);

      } catch (error: any) {

        if (error.response?.status === 404) {
          alert("Aucun formulaire n'a été trouvé.");
        } else {
          alert("Une erreur est survenue.");
        }

        console.error(error);
      }
    };

    loadFormulaire();
  }, [sousSousDomain, sousDomain, api]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError("Géolocalisation non supportée par ce navigateur");
      return;
    }

    setGeoLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoPosition({
          lat: parseFloat(position.coords.latitude.toFixed(6)),
          lng: parseFloat(position.coords.longitude.toFixed(6)),
        });
        setGeoLoading(false);
      },
      (error) => {
        const messages: Record<number, string> = {
          1: "Permission refusée par l'utilisateur",
          2: "Position introuvable",
          3: "Délai d'attente dépassé",
        };
        setGeoError(messages[error.code] || "Erreur de géolocalisation");
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const handleFieldChange = (fieldId: string, value: any) => {
    // Mettre à jour formData
    setFormData((prev) => {
      const newFormData = { ...prev, [fieldId]: value };

      const newVisibleFields: Record<string, boolean> = {};

      formulaire.fields.forEach((f) => {
        const logic = f.conditionalLogic;
        if (logic) {
          const parentId = logic.parentFieldId || `field_${logic.parentContenuId}`;
          const parentVal = newFormData[parentId];

          let visible = false;
          switch (logic.operator) {
            case "equals":
              visible = parentVal === logic.value;
              break;
            case "not_equals":
              visible = parentVal !== logic.value;
              break;
            case "contains":
              visible = Array.isArray(parentVal) && parentVal.includes(logic.value);
              break;
          }
          newVisibleFields[f.id] = visible;
        } else {
          newVisibleFields[f.id] = true;
        }
      });

      
      // Mettre à jour le state
      setVisibleFields(newVisibleFields);

      return newFormData;
    });
  };

  const handleExport = () => {
    if (!formulaire || !formulaire.fields) {
      console.error("Aucun formulaire trouvé");
      return;
    }

    const headers = formulaire.fields.map(f => f.label);

    let csvContent = headers.join(";") + "\n";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = (formulaire.titre || "export") + ".csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearForm = () => {
    setFormData({});
    
    const resetVisibility: Record<string, boolean> = {};
    formulaire.fields.forEach(field => {
      resetVisibility[field.id] = field.conditionalLogic ? false : true;
    });
    setVisibleFields(resetVisibility);
  };

  const handleAddData = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const fd = new FormData(form);

    const data: Record<string, any> = {};
    fd.forEach((value, key) => {
      if (key.endsWith("[]")) {
        const cleanKey = key.replace("[]", "");

        if (!Array.isArray(data[cleanKey])) {
          data[cleanKey] = [];
        }

        data[cleanKey].push(value);
      } else {
        data[key] = value;
      }
    });

    // Vérifier les champs vides
    if (Object.values(data).some(val => String(val).trim() === "")) {
      alert("Veuillez remplir tous les champs avant d'ajouter");
      return;
    }

    let id=0;

    if(submissions.length>0){
      const maxId=Math.max(...submissions.map(s=>s.get("id")));
      id = maxId + 1;
    }

    data["id"]=id;

    const newEntry = new Map<string, any>(
      Object.entries(data)
    );

    setSubmissions(prev => [newEntry, ...prev]);
    setSuccessVisible(true);

    setTimeout(() => {
      setSuccessVisible(false);
      setShowForm(false);
      clearForm();
    }, 1500);

    form.reset();
  };

  const handleDeleteEntry = (id: string) => {
    setSubmissions(prev => prev.filter(entry => entry.get("id") !== id));
  };

  const handleValidateCollection = async () => {
    if(validating){
      return;
    }

    setValidating(true);

    if (!selectedLocality || (!selectedLocality?.idIle && !selectedLocality?.idRegion && !selectedLocality?.idCommune && !selectedLocality?.idLocalite)) {
      setErrorMessage("Veuillez sélectionner une structure avant de valider");
      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 1500);
      setValidating(false);
      return;
    }

    if (!submissions || submissions.length === 0) {
      setErrorMessage("Aucune donnée à valider");
      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 1500);
      setValidating(false);
      return;
    }

    const payload: Record<string, any>[] = submissions.map((item: any) => {
      let obj: Record<string, any>;
      if (item instanceof Map) {
        obj= Object.fromEntries(item);
      }

      else if (typeof item === "object" && item !== null) {
        obj= Object.assign({}, item);
      }

      else{
        throw new Error("Format de submission invalide");
      }

      obj.id_ile = selectedLocality?.idIle || null;
      obj.id_region = selectedLocality?.idRegion || null;
      obj.id_commune = selectedLocality?.idCommune || null;
      obj.id_localite = selectedLocality?.idLocalite || null;
      obj.latitude = geoPosition?.lat ?? null;
      obj.longitude = geoPosition?.lng ?? null;
      obj.id=undefined;
      return obj;
    });

    try {
      if (idSousFenetre !== null && idSousFenetre !== undefined && idSousFenetre!=0) {
        console.log("PAYLOAD :", payload);
	await GroupeImportService.createForSousFenetre(
          api,
          idSousFenetre,
          payload
        );
      } else {
	console.log("PAYLOAD :", payload);
        await GroupeImportService.createForFenetre(
          api,
          idFenetre,
          payload
        );
      }
    } catch (error: any) {
      console.error("Erreur lors de l'import :", error);
      setErrorMessage(error.message || "Erreur lors de l'import");
      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 2000);
      setValidating(false);
      return;
    }

    setSuccessMessage("Collecte validée et ajoutée aux données existantes !");
    setSuccessVisible(true);

    setTimeout(() => {
      setSuccessVisible(false)
      navigate("/agent");
    }, 1500);

    setValidating(false);
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;

      const lines = text.split(/\r\n|\n/).filter(line => line.trim() !== "");
      if (lines.length === 0) {
        console.warn("CSV vide ou mal formatté");
        return;
      }

      const headers = lines[0].split(/[;,]/).map(h => h.trim());

      let id=0;
      if(submissions.length>0){
        const maxId=Math.max(...submissions.map(s=>s.get("id")));
        id = maxId + 1;
      }

      const parsedObjects = lines.slice(1).map(line => {
        const values = line.split(/[;,]/).map(v => v.trim());
        const obj: Record<string, any> = {};

        headers.forEach((header, idx) => {
          obj[header] = values[idx] ?? "";
        });

        obj["id"]=id;
        id+=1;
        return obj;
      });

      const maps = parsedObjects.map(obj => new Map(Object.entries(obj)));
      setSubmissions(prev => [...maps, ...prev]);
    };

    reader.readAsText(file);

    setSuccessMessage("Fichier importé avec succès !");
    setSuccessVisible(true);
    setTimeout(() => setSuccessVisible(false), 1500);
  };

  return (
    <AuthentifiedPage
      leftButtons={[{ text: "Annuler et supprimer", color: "red", onClick: () => {} }]}
      rightButton={{ text: "Valider la collecte", color: "green", onClick: handleValidateCollection }}
    >
      <main className="container py-4 flex-grow-1 page-with-fixed-footer">
        <BackButton text="Retour" />

        <h4 className="mt-3">{formulaire.titre}</h4>
        <CollecteSuccessMessage visible={successVisible} message={successMessage} />
        <CollecteErrorMessage visible={errorVisible} message={errorMessage} />

        {mode === "import" && (
          <div className="mt-4 bg-white p-4 rounded shadow-sm">

            {/* Ligne titre + bouton */}
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Importer un fichier CSV / Excel</h5>

              <button className="btn-export d-flex align-items-center" onClick={handleExport}>
                <strong>Exporter CSV</strong>
              </button>
            </div>

            <input
              type="file"
              className="form-control mt-3"
              onChange={handleFileImport}
            />

            <p className="text-muted mt-2">Formats acceptés : CSV, XLSX, XLS</p>
          </div>

        )}

        {(mode === "manual" || mode === "import") && (
          <div className="mt-3 mb-3 bg-white p-4 rounded shadow-sm">
            
            {mode === "manual" && (
              <div className="col-md-6 mb-4">
                <button
                  className="btn btn-success"
                  onClick={() => setShowForm(!showForm)}
                >
                  <i className="bi bi-plus-circle"></i>{" "}
                  {showForm ? "Masquer le formulaire" : "Ajouter des données"}
                </button>
              </div>
            )}

            <div className="row g-3 align-items-end">
              <div className="col-md-12">
                <label className="form-label fw-bold">
                  Sélectionner une structure <span className="text-danger">*</span>
                </label>
                <LocalitySelector value={selectedLocality} onChange={setSelectedLocality} />
              </div>
            </div>

          </div>
        )}

        {mode === "manual" && showForm && (
          <form id="form" className="row g-3 bg-white p-4 rounded shadow-sm"
            onSubmit={e => { e.preventDefault(); handleAddData(e); }}>

            <DynamicForm
              fields={formulaire.fields}
              visibleFields={visibleFields}
              formData={formData}
              onFieldChange={handleFieldChange}
            />

            <div className="col-12 mt-4">
              <button type="submit" className="btn btn-success">
                <i className="bi bi-check-circle"></i> Ajouter cette donnée
              </button>
            </div>
          </form>
        )}
        <div className="mt-4">
          <CollectDataTable
            schema={formulaire.fields}
            data={submissions}
            onDelete={handleDeleteEntry}
          />
        </div>

        {/* Bloc géolocalisation */}
        <div className="mt-2 mb-3">
          {geoLoading && (
            <span className="badge bg-secondary">
              <i className="bi bi-geo-alt me-1"></i>Localisation en cours…
            </span>
          )}

          {geoPosition && !geoLoading && (
            <span className="badge bg-success">
              <i className="bi bi-geo-alt-fill me-1"></i>
              Position détectée&nbsp;: {geoPosition.lat}, {geoPosition.lng}
            </span>
          )}

          {geoError && !geoLoading && (
            <span className="badge bg-warning text-dark">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {geoError}
            </span>
          )}
        </div>

      </main>
    </AuthentifiedPage>
  );
};

export default CollectDataPage;
